/* global Stripe */
import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {FormattedDate, injectIntl} from 'react-intl';
import loadScript from 'load-script';
import moment from 'moment';
import * as tracking from '../../tracking';
import UpdateCompanySubscriptionDataMutation from '../../mutations/update_company_subscription_data_mutation';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import Button from '../../forecast-app/shared/components/buttons/button/button';
import Payment from '../../components/trial-end/payment.js';
import {account_plan, BUTTON_COLOR, BUTTON_STYLE, TIERS} from '../../constants';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import Util from '../../forecast-app/shared/util/util';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import UploadingOverlay from '../../forecast-app/shared/components/uploading-overlay/uploading_overlay';
import {withRouter} from 'react-router-dom';
import Warning from '../../components/warning';
import {cloneDeep} from 'lodash';
import {personIsClientUser, personIsVirtualUser} from '../../forecast-app/shared/util/PermissionsUtil';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';

let scriptLoadingStarted = false;

class settingsSubscription extends Component {
	constructor(props) {
		super(props);

		this.onApiLoad = this.onApiLoad.bind(this);

		this.state = {
			invoiceEmail: props.viewer.company.subscription ? this.props.viewer.company.subscription.email : null,
			contactPhone: props.viewer.company.contactPhone,
			contactName: props.viewer.company.contactName,
			//contactEmail: props.viewer.company.subscription ? props.viewer.company.subscription.email : null,
			cardName: this.props.viewer.company.subscription ? this.props.viewer.company.subscription.cardholderName : null,
			loading: false,
			upgradeAnnualError: null,
			expanded: true,
			editBilling: false,
			isUpdatingSeats: false,
			partialPaymentExpanded: false,
			removeSeatsReasonSpecified: false,
		};
	}

	componentDidMount() {
		document.title = 'Subscription - Account - Forecast';
		// Segment
		tracking.trackPage('settings-subscription');

		if (this.isStripeReady()) {
			// Stripe is ready
			this.onApiLoad();
		} else if (!scriptLoadingStarted) {
			// load stripe api
			scriptLoadingStarted = true;
			loadScript('https://js.stripe.com/v3/', this.onApiLoad);
		} else {
			// is loading
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.viewer.company.contactName !== this.state.contactName) {
			this.setState({contactName: nextProps.viewer.company.contactName});
		}
		if (nextProps.viewer.company.contactPhone !== this.state.contactPhone) {
			this.setState({contactPhone: nextProps.viewer.company.contactPhone});
		}
		if (nextProps.viewer.company.subscription && nextProps.viewer.company.subscription.email !== this.state.invoiceEmail) {
			this.setState({invoiceEmail: nextProps.viewer.company.subscription.email});
		}
	}

	onApiLoad() {
		// Create a Stripe client
		this.stripe = Stripe(process.env.STRIPE_PUBLIC_KEY);
	}

	isStripeReady() {
		return !!window.Stripe;
	}

	upgradeToProfessional() {
		tracking.trackEvent('Admin Buy Now Clicked');
		this.props.history.push('/trial-end#select_plan');
	}

	toggleEdit(e) {
		this.setState({editBilling: !this.state.editBilling});
	}

	toggleExpand() {
		this.setState({expanded: !this.state.expanded});
	}

	togglePartialExpand() {
		this.setState({partialPaymentExpanded: !this.state.partialPaymentExpanded});
	}

	handleFieldChange(property, e) {
		this.setState({[property]: e.target ? e.target.value : e});
	}

	handleRemoveSeatRequest(seats, virtualSeats, reason, reasonElaborated) {
		if (process.env.CIRCLE_BRANCH === 'production') {
			if (reason !== '') {
				this.setState({removeSeatsReasonSpecified: true});
			}
			this.setState({isUpdatingSeats: true});
			const onSuccess = seatRequestSuccess => {
				this.setState({isUpdatingSeats: false});
				createToast({duration: 5000, message: seatRequestSuccess ? 'Seat Update Request has been sent' : 'Request could not be sent'});
			};

			const seatRequest = {
				companyName: this.props.viewer.company.name,
				userEmail: this.props.viewer.email,
				removeSeats: seats !== '' ? seats : 0,
				removeVirtualSeats: virtualSeats !== '' ? virtualSeats : 0,
				reason: reason,
				reasonElaborated: reasonElaborated,
			};

			const headers = new Headers();
			headers.append('Content-Type', 'application/json');
			const init = {
				method: 'POST',
				headers,
				credentials: 'include',
				body: JSON.stringify(seatRequest),
			};

			fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'remove_seat_request', init)
				.then(res => res.json())
				.then(() => {
					onSuccess(true);
				})
				.catch(() => {
					onSuccess(false);
				});
		}
	}

	updateSeats(newSeats, newVirtualSeats, shouldAdd) {
		// Show toast
		if (!(newSeats + newVirtualSeats) || newSeats > 100 || newVirtualSeats > 100) return;
		this.setState({isUpdatingSeats: true});
		const onSuccess = result => {
			this.setState({isUpdatingSeats: false});
			createToast({duration: 5000, message: shouldAdd ? 'Seats Added' : 'Seats Removed'});
		};
		// Do we use seats or userSeats?
		const isChargebeeCustomer = this.props.viewer.company.isChargebeeCustomer;

		if (isChargebeeCustomer) {
			const subscription = this.props.viewer.company.subscription;
			if (newSeats + newVirtualSeats > 0) {
				const uSeats = shouldAdd ? subscription.seats + Number(newSeats) : undefined;
				const mutationObject = {id: this.props.viewer.company.id};
				if (uSeats) {
					mutationObject.userSeats = uSeats;
				}
				// Chargebee plans have virtual seats as well
				const isChargebeePlan = subscription.planId.includes('chargebee');
				if (isChargebeePlan) {
					mutationObject.virtualSeats = shouldAdd ? subscription.virtualSeats + Number(newVirtualSeats) : subscription.virtualSeats - Number(newVirtualSeats);
				}
				Util.CommitMutation(UpdateCompanySubscriptionDataMutation, mutationObject, onSuccess, true, false);
			}
		}
	}

	// ATTENTION: We apparently don't allow removing seats on non-production environments. It is not clear why as this was made by Jonas who no longer works here.
	// This needs some thorough investigation to discover why.
	handleSeatsUpdate(newSeats, newVirtualSeats, shouldAdd, reason, reasonElaborated) {
		if (!shouldAdd) {
			if (newSeats > 0) {
				this.handleRemoveSeatRequest(newSeats, 0, reason, reasonElaborated);
			}
			if (newVirtualSeats > 0) {
				this.updateSeats(0, newVirtualSeats, shouldAdd);
			}
		} else {
			this.updateSeats(newSeats, newVirtualSeats, shouldAdd);
		}
	}

	showSeatsModal(modalType, freeVirtualUsers) {
		const {formatMessage} = this.props.intl;
		const isAdd = modalType === 'add';

		const subscription = this.props.viewer.company.subscription;
		// Chargebee customers use userSeats (and virtual seats if chargebee plan), stripe customers use seats
		const isChargebeePlan = subscription.planId.includes('chargebee');

		const numSeats = subscription.seats;
		const numVirtualSeats = isChargebeePlan ? subscription.virtualSeats : 0;

		const allPersons = this.props.viewer.company.allPersons.edges;

		let activated = allPersons.filter(person => person.node.active);
		activated = activated.map(p => p.node);
		let [users, virtualUsers] = activated.reduce(([user, virtual], e) => (personIsVirtualUser(e) ? [user, [...virtual, e]] : [[...user, e], virtual]), [[], []]);

		// For chargebee plans, we don't play for clients
		if (isChargebeePlan) {
			users = users.filter(e => !personIsClientUser(e));
			virtualUsers = virtualUsers.filter(e => e.client == null);
		}

		showModal({
			type: MODAL_TYPE.SEAT_MANAGEMENT,
			isAdd: isAdd,
			isChargebee: isChargebeePlan,
			freeVirtualUsers,
			showCardSection: this.props.viewer.company.subscription.cardType != null,
			cardLast4: this.props.viewer.company.subscription.cardLast4,
			cardType: this.props.viewer.company.subscription.cardType,
			cardExpirationDate: this.props.viewer.company.subscription.expirationDate,
			numSeats: numSeats,
			curSeats: users.length,
			curVirtualSeats: virtualUsers.length,
			numVirtualSeats: numVirtualSeats,
			minNumSeats: Math.max(this.props.viewer.company.minimumSeats, users.length),
			minNumVirtualSeats: Math.max(this.props.viewer.company.minimumVirtualSeats, virtualUsers.length),
			removeSeatsReason: this.state.removeSeatsReasonSpecified ? 'PLEASE SELECT A REASON' : '',
			title: isAdd ? formatMessage({id: 'settings_subscription.seats-add'}) : formatMessage({id: 'settings_subscription.seats-remove-request'}),
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
					cy: 'cancel-modal-button',
				},
				isAdd
					? {
							text: formatMessage({id: 'common.add'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.GREEN,
							cy: 'add-seats-modal-button',
							callback: (newSeats, newVirtualSeats) => this.handleSeatsUpdate(newSeats, newVirtualSeats, true),
							mayBeLocked: true,
					  }
					: {
							text: formatMessage({id: 'settings_subscription.send-remove-request'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.GREEN,
							cy: 'remove-seats-modal-button',
							callback: (newSeats, newVirtualSeats, reason, reasonElaborated) => this.handleSeatsUpdate(newSeats, newVirtualSeats, false, reason, reasonElaborated),
							mayBeLocked: true,
					  },
			],
		});
	}

	formatExpiryDate(expiryDateString) {
		const parts = expiryDateString.split('/');
		const month = parts[0].length === 1 ? '0' + parts[0] : parts[0];
		const year = parts[1];
		return month + '/' + year;
	}

	payUnpaidNowUsingCurrentCard() {
		this.setState({loading: true});
		fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'stripe_3ds_confirm', {
			credentials: 'include',
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			body: JSON.stringify({
				pay_using_current_card: true,
				amount: this.props.viewer.company.subscription.unpaid,
			}),
		}).then(result => {
			// Handle server response
			result.json().then(json => {
				this.handleServerResponse(json);
			});
		});
	}

	handleServerResponse(response) {
		if (response.error) {
			// Show error from server on payment form
			// Show error in payment form
			showModal({
				type: MODAL_TYPE.GENERIC,
				content: (
					<div className="default-warning-modal">
						<Warning messageId="settings_subscription.payment_failed" />
						<div className="warning-part-2">{response.error.message}</div>
					</div>
				),
				buttons: [
					{
						text: this.props.intl.formatMessage({id: 'common.ok'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.PURPLE,
					},
				],
			});
			this.setState({loading: false});
		} else if (response.status === 400) {
			showModal({
				type: MODAL_TYPE.GENERIC,
				content: (
					<div className="default-warning-modal">
						<Warning messageId="settings_subscription.payment_failed" />
						<div className="warning-part-2">{response.message ? response.message : 'An error occurred. Please try again or try another card'}</div>
					</div>
				),
				buttons: [
					{
						text: this.props.intl.formatMessage({id: 'common.ok'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.PURPLE,
					},
				],
			});
			this.setState({loading: false});
		} else if (response.requires_action) {
			// Use Stripe.js to handle required card action
			this.handleAction(response);
		} else {
			fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'stripe_3ds_confirm', {
				credentials: 'include',
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({
					payment_intent_id: response.payment_intent_id,
					pay_using_current_card: true,
					amount: this.props.viewer.company.subscription.unpaid,
				}),
			}).then(res => {
				this.setState({loading: false});
			});
		}
	}

	handleAction(response) {
		this.stripe.handleCardAction(response.payment_intent_client_secret).then(result => {
			if (result.error) {
				showModal({
					type: MODAL_TYPE.GENERIC,
					content: (
						<div className="default-warning-modal">
							<Warning messageId="settings_subscription.payment_failed" />
							<div className="warning-part-2">{result.error.message}</div>
						</div>
					),
					buttons: [
						{
							text: this.props.intl.formatMessage({id: 'common.ok'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.PURPLE,
						},
					],
				});
				this.setState({loading: false});
			} else {
				// The card action has been handled
				// The PaymentIntent can be confirmed again on the server
				fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'stripe_3ds_confirm', {
					credentials: 'include',
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						payment_intent_id: result.paymentIntent.id,
					}),
				})
					.then(response => response.json())
					.then(confirmResult => {
						this.handleServerResponse(confirmResult);
					});
			}
		});
	}

	handleSubscriptionUpgrade() {
		switch (this.props.viewer.company.subscription.planId.toUpperCase()) {
			case account_plan.CHARGEBEE_LITE_MONTHLY_2020:
			case account_plan.CHARGEBEE_LITE_QUARTERLY_2020:
			case account_plan.CHARGEBEE_LITE_YEARLY_2020:
			case account_plan.CHARGEBEE_LITE_MONTHLY_2020_GBP:
			case account_plan.CHARGEBEE_LITE_QUARTERLY_2020_GBP:
			case account_plan.CHARGEBEE_LITE_YEARLY_2020_GBP:
			case account_plan.CHARGEBEE_LITE_MONTHLY_2020_EUR:
			case account_plan.CHARGEBEE_LITE_QUARTERLY_2020_EUR:
			case account_plan.CHARGEBEE_LITE_YEARLY_2020_EUR:
			case account_plan.CHARGEBEE_LITE_MONTHLY_2020_DKK:
			case account_plan.CHARGEBEE_LITE_QUARTERLY_2020_DKK:
			case account_plan.CHARGEBEE_LITE_YEARLY_2020_DKK:
			case account_plan.CHARGEBEE_PRO_MONTHLY_2020:
			case account_plan.CHARGEBEE_PRO_QUARTERLY_2020:
			case account_plan.CHARGEBEE_PRO_YEARLY_2020:
			case account_plan.CHARGEBEE_PRO_MONTHLY_2020_GBP:
			case account_plan.CHARGEBEE_PRO_QUARTERLY_2020_GBP:
			case account_plan.CHARGEBEE_PRO_YEARLY_2020_GBP:
			case account_plan.CHARGEBEE_PRO_MONTHLY_2020_EUR:
			case account_plan.CHARGEBEE_PRO_QUARTERLY_2020_EUR:
			case account_plan.CHARGEBEE_PRO_YEARLY_2020_EUR:
			case account_plan.CHARGEBEE_PRO_MONTHLY_2020_DKK:
			case account_plan.CHARGEBEE_PRO_QUARTERLY_2020_DKK:
			case account_plan.CHARGEBEE_PRO_YEARLY_2020_DKK:
				showModal({
					type: MODAL_TYPE.UPGRADE_SUBSCRIPTION_NEW,
					subscription: {
						plan: this.props.viewer.company.subscription.planId.toUpperCase(),
						userSeats: this.props.viewer.company.subscription.seats,
						virtualSeats: this.props.viewer.company.subscription.virtualSeats,
						coupons: this.props.viewer.company.subscription.coupons,
						currency: this.props.viewer.company.subscription.currency,
						seatPrice: this.props.viewer.company.subscription.seatPrice / 100,
						billingPeriod: this.props.viewer.company.subscription.billingPeriod,
						billingPeriodType: this.props.viewer.company.subscription.billingPeriodType,
					},
					country: this.props.viewer.company.country,
					companyId: this.props.viewer.company.id,
					planList: this.props.viewer.chargebeePlan,
					addonList: this.props.viewer.chargebeeAddon,
				});
				break;
			default:
				return <div />;
		}
	}

	getHeader() {
		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={this.props.intl.formatMessage({id: 'settings.subscription'})} />
			</TopHeaderBarWrapper>
		);
	}

	render() {
		const {formatDate, formatMessage} = this.props.intl;
		const subscription = this.props.viewer.company.subscription;
		const isChargebeePlan = subscription.planId.includes('chargebee');
		const numSeats = subscription && subscription.seats; //isChargebeeCustomer ? this.props.viewer.company.userSeats : this.props.viewer.company.seats;
		const numVirtualSeats = subscription && subscription.virtualSeats; //this.props.viewer.company.virtualSeats;
		const currencySymbol = subscription && Util.GetCurrencySymbol(subscription.currency);
		const showUpgradeButton = [TIERS.LITE, TIERS.PRO].includes(this.props.viewer.company.tier);

		const freeVirtualUsers = this.props.viewer.company.planAddons.some(pa => pa === 'free_virtual_users');

		if (subscription === null) {
			return (
				<div className="section-content settings-app settings-subscription-v2">
					{this.props.children}
					{this.getHeader()}
					<div className="section-body">
						<div className="inner">
							<fieldset className="current-subscription">
								<h3>{formatMessage({id: 'settings_subscription.no-subscription-yet'})}</h3>
								{showUpgradeButton ? (
									<Button
										className="upgrade-now"
										cy="upgrade-to-plan"
										text={formatMessage({id: 'settings.subscription.upgrade_plan'})}
										buttonStyle={BUTTON_STYLE.OUTLINE}
										colorTheme={BUTTON_COLOR.PURPLE}
										onClick={this.upgradeToProfessional.bind(this)}
									/>
								) : null}
							</fieldset>
						</div>
					</div>
				</div>
			);
		}

		let users = this.props.viewer.company.allPersons.edges.filter(edge => !personIsVirtualUser(edge.node) && (isChargebeePlan ? !personIsClientUser(edge.node) : true)).length;
		let virtualUsers = this.props.viewer.company.allPersons.edges.filter(edge => personIsVirtualUser(edge.node) && edge.node.client == null).length;

		const planPeriod = subscription.planId.toUpperCase().includes('MONTHLY')
			? formatMessage({id: 'common.monthly'})
			: subscription.planId.toUpperCase().includes('QUARTERLY')
			? formatMessage({id: 'common.quarterly'})
			: formatMessage({id: 'common.annual'});

		const planText = subscription.planId.toUpperCase().includes('_LITE_')
			? formatMessage({id: 'select_plan.lite_plan_name'}).toUpperCase()
			: subscription.planId.toUpperCase().includes('_PRO_')
			? formatMessage({id: 'select_plan.pro_plan_name'}).toUpperCase()
			: subscription.planId.toUpperCase().includes('PREMIUM')
			? formatMessage({id: 'select_plan.premium_plan_name'}).toUpperCase()
			: subscription.planId.toUpperCase().includes('PROFESSIONAL')
			? formatMessage({id: 'select_plan.professional_plan_name'}).toUpperCase()
			: formatMessage({id: 'select_plan.enterprise_plan_name'}).toUpperCase();
		const planEndDate = Util.CreateMomentDate(subscription.nextPaymentDateYear, subscription.nextPaymentDateMonth, subscription.nextPaymentDateDay);
		const endDateString = formatDate(planEndDate, {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
		const isCard = !!subscription.cardType;

		const nextPartialAmount = subscription.nextPartialAmount;

		let nextPartialBillingDate = moment().date(20);
		if (nextPartialBillingDate.isBefore(moment())) {
			nextPartialBillingDate.add(1, 'month');
		}
		const history = cloneDeep(subscription.history);
		history.sort((a, b) => {
			if (a.dateYear && b.dateYear) {
				const aDate = Util.CreateNonUtcMomentDate(a.dateYear, a.dateMonth, a.dateDay);
				const bDate = Util.CreateNonUtcMomentDate(b.dateYear, b.dateMonth, b.dateDay);
				return bDate - aDate;
			}
			return 0;
		});

		return (
			<div className="section-content settings-app settings-subscription-v2">
				{this.props.children}
				{this.getHeader()}
				{this.state.editBilling ? (
					<div>
						<div className="back-wrapper" onClick={this.toggleEdit.bind(this)} tabIndex={0}>
							<div className={'back-arrow-control'} />
							<div className="back-text"> {formatMessage({id: 'settings.subscription'})} </div>
						</div>
						<Payment
							changeInvoiceInformation={!isCard}
							changeCreditCard={isCard}
							billing={this.props.viewer.company.subscription.planId.includes('month') ? 'monthly' : 'annual'}
							paymentType={isCard ? 'card' : 'invoice'}
							cardName={this.state.cardName}
							invoiceEmail={this.state.invoiceEmail}
							contactPhone={this.state.contactPhone}
							contactName={this.state.contactName}
							viewer={this.props.viewer}
							handleFieldChange={this.handleFieldChange.bind(this)}
							toggleEdit={this.toggleEdit.bind(this)}
							setPage={this.toggleEdit.bind(this)}
							couponId={''}
							cardLast4={this.props.viewer.company.subscription.cardLast4}
						/>
					</div>
				) : (
					<>
						{this.state.loading ? <UploadingOverlay /> : null}
						<CustomScrollDiv>
							<div className="section-body">
								<div className="inner">
									<div className="card">
										<div className="plan">
											<div className="plan-type-wrapper">
												<div className="your-plan">{formatMessage({id: 'settings_subscription.your_plan'})}</div>
												<div data-cy="subscription-header" className="subscription-header small-top-margin">
													{planText}
												</div>
											</div>
											{showUpgradeButton ? (
												<div className="upgrade-plan-button">
													<Button
														buttonStyle={BUTTON_STYLE.OUTLINE}
														colorTheme={BUTTON_COLOR.PURPLE}
														text={formatMessage({id: 'settings_subscription.upgrade'})}
														onClick={this.handleSubscriptionUpgrade.bind(this)}
														cy="upgrade-plan-button"
													/>
												</div>
											) : (
												<div />
											)}
										</div>
										<hr />
										<div className="payment">
											<div className="payment-tile-wrapper">
												<div className={'data-tile white-space'} />
												{subscription.nextAmountDue ? (
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.next-payment-date'})}</label>
														<span className="tile-value date">
															{formatDate(new Date(subscription.nextPaymentDateYear, subscription.nextPaymentDateMonth - 1, subscription.nextPaymentDateDay), {
																day: 'numeric',
																month: 'long',
																year: 'numeric',
															})}
														</span>
													</div>
												) : null}
												{subscription.nextAmountDue ? (
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.amount'})}</label>
														<span className="tile-value">{Util.getFormattedCurrencyValue(currencySymbol, this.props.intl.formatNumber(subscription.nextAmountDue))}</span>
													</div>
												) : null}
												{nextPartialAmount && subscription.billingPeriodType === 'YEAR' ? (
													<>
														<div className="data-tile">
															<label>Next partial payment date</label>
															<span className="tile-value date">
																{formatDate(nextPartialBillingDate.toDate(), {
																	day: 'numeric',
																	month: 'long',
																	year: 'numeric',
																})}
															</span>
														</div>
														<div className={'data-tile'}>
															<label>Next partial amount</label>
															<span className={'tile-value'}>
																<span>{Util.getFormattedCurrencyValue(currencySymbol, this.props.intl.formatNumber(subscription.nextPartialAmount))}</span>
																<div className={'expand-control ' + (this.state.partialPaymentExpanded === true ? 'collapse' : 'expand')} onClick={this.togglePartialExpand.bind(this)} tabIndex={0} />
															</span>
															{this.state.partialPaymentExpanded && (
																<table className="partial-payment-lines">
																	<tbody>
																		{subscription.partialPaymentLines.map(ppl => (
																			<tr key={ppl.id}>
																				<td>{ppl.description}</td>
																				<td className="amount">{Util.getFormattedNumberWithCurrency(currencySymbol, ppl.amount, this.props.intl)}</td>
																			</tr>
																		))}
																	</tbody>
																</table>
															)}
														</div>
													</>
												) : null}

												<div className={'data-tile'}>
													<label>{formatMessage({id: 'common.billing_period'})}</label>
													<span data-cy="billing-period" className={'tile-value'}>
														{planPeriod}
													</span>
												</div>
												{subscription.credit && (
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.credit'})}</label>
														<span className="tile-value">{'$' + this.props.intl.formatNumber(subscription.credit)}</span>
													</div>
												)}
												<div className={'data-tile white-space'} />
											</div>
										</div>
									</div>
									<div className="seats">
										<div className="licensing">
											<div className="header-wrapper">
												<div className="subscription-header">{formatMessage({id: 'select_plan.licensing'})}</div>
												<div className="buttons">
													{this.state.isUpdatingSeats ? (
														<div className={'updating-wrapper'}>
															<UploadingOverlay />
														</div>
													) : null}
													<Button
														className={'seats-button'}
														buttonStyle={BUTTON_STYLE.FILLED}
														colorTheme={BUTTON_COLOR.WHITE}
														text={formatMessage({id: 'settings_subscription.seats-add'}).toUpperCase()}
														onClick={this.showSeatsModal.bind(this, 'add', freeVirtualUsers)}
														cy={'add-seats-button'}
														isDisabled={this.state.isUpdatingSeats}
													/>
													<span className={'seats-dash'}>/ </span>
													<Button
														className={'seats-button'}
														buttonStyle={BUTTON_STYLE.FILLED}
														colorTheme={BUTTON_COLOR.WHITE}
														text={formatMessage({id: 'settings_subscription.seats-remove'}).toUpperCase()}
														onClick={this.showSeatsModal.bind(this, 'remove', freeVirtualUsers)}
														cy={'remove-seats-button'}
														isDisabled={this.state.isUpdatingSeats}
													/>
												</div>
											</div>
											<table className={'license-table'} data-cy={'license-table'}>
												<thead>
													<tr>
														<th className={'white-space'} />
														<th>{formatMessage({id: 'settings_subscription.seat_type'})}</th>
														<th>{formatMessage({id: 'settings_subscription.total_seats'}).toUpperCase()}</th>
														<th>{formatMessage({id: 'common.assigned'}).toUpperCase()}</th>
														<th>{formatMessage({id: 'cards.unassigned'}).toUpperCase()}</th>
														<th className={'white-space'} />
													</tr>
												</thead>
												<tbody>
													<tr>
														<td className={'white-space'} />
														<td>{formatMessage({id: 'settings_subscription.users'})}</td>
														<td data-cy={'licensed-seats'}>{numSeats}</td>
														<td data-cy={'filled-seats'}>{users}</td>
														<td data-cy={'available-seats'}>{numSeats - users}</td>
														<td className={'white-space'} />
													</tr>
													{isChargebeePlan && !freeVirtualUsers ? (
														<tr>
															<td className={'white-space'} />
															<td>{formatMessage({id: 'settings_subscription.virtual_users'})}</td>
															<td data-cy={'licensed-virtual-seats'}>{numVirtualSeats}</td>
															<td data-cy={'filled-virtual-seats'}>{virtualUsers}</td>
															<td data-cy={'available-virtual-seats'}>{numVirtualSeats - virtualUsers}</td>
															<td className={'white-space'} />
														</tr>
													) : null}
												</tbody>
											</table>
										</div>
									</div>
									<div className="billing">
										<div className="subscription-header">{formatMessage({id: 'settings_subscription.billing-information'})}</div>

										<div className="card">
											<div className="header-wrapper">
												<div className="invoice-email">
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.invoice-email'})}</label>
														<span className="tile-value">{subscription.email}</span>
													</div>
												</div>
												<div className="edit-billing-button">
													<button className={'edit-billing' + (this.state.editBilling ? ' active' : ' inactive')} onClick={this.toggleEdit.bind(this)} data-cy={'edit-billing-info'} />
												</div>
											</div>
											<hr />
											{isCard ? (
												<div className="tile-container">
													<div className={'white-space'} />
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.cardholder-name'})}</label>
														<div className="tile-value">{subscription.cardholderName}</div>
													</div>
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.card-type'})}</label>
														<span className="tile-value">{subscription.cardType}</span>
													</div>
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.card-last-four-digits'})}</label>
														<span className="tile-value">{subscription.cardLast4}</span>
													</div>
													<div className="data-tile">
														<label>{formatMessage({id: 'settings_subscription.card-exp-date'})}</label>
														<span className="tile-value">{this.formatExpiryDate(subscription.expirationDate)}</span>
													</div>
													<div className={'white-space'} />
												</div>
											) : (
												<div className="tile-container">
													<div className="data-tile">
														<label>{formatMessage({id: 'common.contact_name'})}</label>
														<span className="tile-value">{this.props.viewer.company.contactName}</span>
													</div>
													<div className="data-tile">
														<label>{formatMessage({id: 'common.contact_phone'})}</label>
														<span className="tile-value">{this.props.viewer.company.contactPhone}</span>
													</div>
												</div>
											)}
										</div>
									</div>
									<div className="invoice-header-wrapper">
										<div className="subscription-header">{formatMessage({id: 'settings_subscription.billing-history'})}</div>
										<div className={'expand-control ' + (this.state.expanded === true ? 'collapse' : 'expand')} onClick={this.toggleExpand.bind(this)} tabIndex={0} />
										{subscription.unpaid !== null && subscription.unpaid !== 0 && isCard && (
											<div className="unpaid-wrapper">
												<span className="unpaid-text">Unpaid {Util.getFormattedCurrencyValue(currencySymbol, this.props.intl.formatNumber(subscription.unpaid))}</span>{' '}
												<Button className="unpaid-button" onClick={this.payUnpaidNowUsingCurrentCard.bind(this)} buttonStyle={BUTTON_STYLE.FILLED} colorTheme={BUTTON_COLOR.RED} text="Pay now using current card" />
											</div>
										)}
									</div>
									{this.state.expanded ? (
										<div className="invoice">
											<table className="billing-history-table">
												<thead>
													<tr>
														<th className={'white-space'} />
														<th>{formatMessage({id: 'settings_subscription.date'}).toUpperCase()}</th>
														<th>{formatMessage({id: 'common.type'}).toUpperCase()}</th>
														<th>{formatMessage({id: 'settings_subscription.amount'}).toUpperCase()}</th>
														<th>{formatMessage({id: 'common.status'}).toUpperCase()}</th>
														<th className={'white-space'} />
													</tr>
												</thead>
												<tbody>
													{history.map((history, index) => (
														<tr className={'billing-history-row'} key={index}>
															<td className={'white-space'} />
															<td>
																<FormattedDate day="numeric" month="long" year="numeric" value={new Date(history.dateYear, history.dateMonth - 1, history.dateDay)} />
															</td>
															<td>{history.type}</td>
															<td>
																{history.type === 'Credit Note' ? '-' : ''}
																{Util.getFormattedCurrencyValue(currencySymbol, this.props.intl.formatNumber(history.amount))}
															</td>
															<td>{history.paid ? formatMessage({id: 'settings_subscription.paid'}) : formatMessage({id: 'settings_subscription.unpaid'})}</td>
															{history.invoiceUrl ? (
																<td>
																	<a href={history.invoiceUrl} target="_blank" rel="noopener noreferrer">
																		Pay now
																	</a>
																</td>
															) : (
																<td className="white-space" />
															)}
														</tr>
													))}
												</tbody>
											</table>
										</div>
									) : (
										<hr className={'full-width-hr'} />
									)}
								</div>
								<div className="subscription-header">{formatMessage({id: 'settings_subscription.cancel_subscription'})}</div>
								<div className="cancel-subscription-text">{formatMessage({id: 'settings_subscription.cancellation_text'}, {date: endDateString})}.</div>
								<div className="cancel-subscription-button">
									<form action="https://experience.forecast.it/cancellation" method="get" target="_blank">
										<Button buttonStyle={BUTTON_STYLE.OUTLINE} className="red-border" colorTheme={BUTTON_COLOR.RED} text={formatMessage({id: 'settings_subscription.cancel_subscription'})} />
									</form>
								</div>
							</div>
						</CustomScrollDiv>
					</>
				)}
			</div>
		);
	}
}

const settingsSubscriptionQuery = graphql`
	query settingsSubscription_Query {
		viewer {
			actualPersonId
			component(name: "settings_subscription")
			company {
				isChargebeeCustomer
			}
			...settingsSubscription_viewer
		}
	}
`;

graphql`
	fragment settingsSubscription_subscription on Subscription {
		planId
		billingPeriod
		billingPeriodType
		currency
		email
		cardLast4
		cardType
		cardholderName
		expirationDate
		nextPaymentDateYear
		nextPaymentDateMonth
		nextPaymentDateDay
		nextAmountDue
		credit
		nextPartialAmount
		unpaid
		seats
		virtualSeats
		seatPrice
		partialPaymentLines {
			id
			amount
			description
		}
		history {
			dateYear
			dateMonth
			dateDay
			amount
			type
			paid
			invoiceUrl
		}
		coupons {
			id
			amountOff
			duration
			durationInMonths
			percentOff
		}
	}
`;

export {settingsSubscriptionQuery};

export default injectIntl(
	withRouter(
		createFragmentContainer(settingsSubscription, {
			viewer: graphql`
				fragment settingsSubscription_viewer on Viewer {
					id
					email
					availableFeatureFlags {
						key
					}
					company {
						id
						name
						street
						zip
						city
						country
						vatId
						trialEndYear
						trialEndMonth
						trialEndDay
						trialEndTime
						tier
						planAddons
						minimumSeats
						minimumVirtualSeats
						userSeats
						virtualSeats
						contactPhone
						contactName
						contactEmail
						currency
						isChargebeeCustomer
						allPersons(first: 10000, onlyActive: true) {
							edges {
								node {
									id
									permissions
									active
									client {
										id
									}
								}
							}
						}
						subscription {
							...settingsSubscription_subscription @relay(mask: false)
						}
					}
					chargebeePlan {
						chargebeeId
						currency
						price
						period
						periodUnit
					}
					chargebeeAddon {
						chargebeeId
						currency
						price
					}
				}
			`,
		})
	)
);
